/**
 * One column of an import becoming one field of a contact — "city_name (Pune,
 * Nashik) → PROFILE City · 96%". The concept's `.bp-maprow`.
 *
 * ## WHY A ROW WITH AN ARROW, AND NOT A TABLE
 *
 * The upload screen used to draw the mapping as a table: column, then a
 * select. A table says "here are some cells"; the thing a person is deciding
 * is a *transformation* — this column becomes that field — and the arrow is
 * that sentence. Each row is a list item in a `<ul>`, so a screen reader still
 * hears "list, 12 items" and can count them, and the arrow is `aria-hidden`
 * with its word (`arrowLabel`, default "becomes") in `sr-only` text, so the
 * row reads "city_name becomes City" rather than "city_name right arrow".
 *
 * The same row serves the admin's pending changes ("Plan: Reach becomes Reach,
 * Connect") with a different `arrowLabel`, which is why the arrow's word is a
 * prop and not baked in.
 *
 * ## LAYOUT
 *
 * Four columns from `sm` up — source (150–210px), the arrow, the target, an
 * aside such as a `CoverageMeter` — and stacked below `sm`, where the arrow
 * hides and the sr-only word still reads. Radius is `rounded-base`: the
 * concept's 10px snaps to the product's 12. The resting fill is
 * `bg-white/[0.04]`, the concept's 0.03 moved onto the tint ladder.
 * `emphasis` (the concept's `is-birth`, "this column makes a new field") is
 * a violet hairline over `brand-a/8`.
 *
 * ## THE HOLE IT LEAVES
 *
 * `MappingSource` prints at most three sample values and trusts the caller to
 * have chosen safe ones. Samples come from a person's upload and can be real
 * phone numbers or addresses; a screen that shows samples of a contact column
 * must mask them before they get here, exactly as `PersonRow` requires.
 */
import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '../lib/cn';

export interface MappingRowProps extends Omit<HTMLAttributes<HTMLLIElement>, 'children'> {
  /** The source: usually a `MappingSource` (column name plus samples). */
  from: ReactNode;
  /** The target: a FieldChip, a `Select appearance="chip"`, or text. */
  to: ReactNode;
  /** Right-hand slot — a CoverageMeter. */
  aside?: ReactNode;
  /** A small muted note under the target. */
  note?: ReactNode;
  /** Highlight a row that creates something new. */
  emphasis?: boolean;
  /** The word the arrow stands for, read by screen readers. Default "becomes". */
  arrowLabel?: string;
}

export function MappingRow({
  from,
  to,
  aside,
  note,
  emphasis = false,
  arrowLabel = 'becomes',
  className,
  ...rest
}: MappingRowProps) {
  return (
    <li
      className={cn(
        'grid items-center gap-x-3 gap-y-2 rounded-base border px-3 py-2.5 text-[0.84rem]',
        'sm:grid-cols-[minmax(150px,210px)_18px_1fr_auto]',
        emphasis ? 'border-brand-b/45 bg-brand-a/8' : 'border-line bg-white/[0.04]',
        className,
      )}
      {...rest}
    >
      <div className="min-w-0">{from}</div>
      <span aria-hidden="true" className="hidden font-mono text-brand-ink sm:block">
        →
      </span>
      <div className="flex min-w-0 flex-col gap-1">
        <span className="sr-only">{arrowLabel} </span>
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1.5">{to}</div>
        {note !== undefined && note !== null && <p className="text-[0.72rem] text-muted">{note}</p>}
      </div>
      {aside !== undefined && aside !== null ? <div className="justify-self-start sm:justify-self-end">{aside}</div> : <span />}
    </li>
  );
}

export type MappingListProps = HTMLAttributes<HTMLUListElement>;

/** The `<ul>` a set of MappingRows sits in. */
export function MappingList({ className, ...rest }: MappingListProps) {
  return <ul className={cn('grid gap-1.5', className)} {...rest} />;
}

export interface MappingSourceProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** The column's name as it appears in the file. */
  column: string;
  /** Up to three sample values, already safe to show. Extras are dropped. */
  samples?: readonly string[];
  /** Brighter column name, for an emphasised row. */
  emphasis?: boolean;
}

/** A column name in mono, with up to three sample chips under it. */
export function MappingSource({ column, samples = [], emphasis = false, className, ...rest }: MappingSourceProps) {
  const shown = samples.slice(0, 3);
  return (
    <div className={cn('flex min-w-0 flex-col gap-1', className)} {...rest}>
      <code className={cn('break-all font-mono text-[0.78rem]', emphasis ? 'text-brand-ink-strong' : 'text-text')}>
        {column}
      </code>
      {shown.length > 0 && (
        <span className="flex flex-wrap gap-1">
          {shown.map((sample, index) => (
            <span
              key={`${index}-${sample}`}
              className="inline-flex max-w-full items-center truncate rounded-xs border border-line bg-white/[0.04] px-[7px] py-px font-mono text-[0.7rem] text-muted"
            >
              {sample}
            </span>
          ))}
        </span>
      )}
    </div>
  );
}
